import { useCallback, ReactNode, useState, useEffect, useMemo, useRef } from 'react';
import {
  DataGridProProps,
  GridColDef,
  GridFilterModel,
  GridRenderCellParams,
  GridRowSelectionModel,
  GridToolbarColumnsButton,
  GridToolbarFilterButton
} from '@mui/x-data-grid-pro';
import {
  Alert, Box, FormControl, FormHelperText, FormLabel, SelectChangeEvent, Stack, Typography
} from '@mui/material';
import { DateTime } from 'luxon';
import {
  Control, Controller, FormState, useForm, UseFormGetValues,
  UseFormReset, UseFormSetValue
} from 'react-hook-form';
import {
  Leader, ORGANIZATION_PERMISSION,
  OrganizationLicense, User, Organization, OrganizationSubmission,
  OrganizationLeader
} from '../../../store';
import {
  CabDropdown, CabTextInput, CabIcon, CabButton, CabTooltip, CabModal, CabCardSelect, CabList,
} from '@CabComponents';
import colors from '../../../colors';
import { emailRegex, NEW_TIER, NEW_TIER_DETAILS, NEW_TIER_IDS, TIER, USER_ROLE } from '../../../constants';
import { GridData } from './types';
import { CabDropdownProps } from '@CabComponents/CabDropdown';
import { CabButtonProps } from '@CabComponents/CabButton';
import { IoCheckmarkCircleOutline, IoCloseCircleOutline, IoArrowBack, IoTrashOutline } from 'react-icons/io5';
import CabDataGrid from '@CabComponents/CabDataGrid';
import { emptyRowSelectionModel, getSelectedRowIds } from '../../DataGrid/rowSelection';

const dropDownToAddLicenseText = (
  current_tier: NEW_TIER, tier: NEW_TIER,  missingLicenses: boolean, licenseCount: number
) => {
  if (current_tier === NEW_TIER.CORE && tier === NEW_TIER.CORE && licenseCount > 1) {
    return "Upgrade to Strategic to add more licenses";
  }
  if (current_tier !== NEW_TIER.CORE && tier === NEW_TIER.CORE && licenseCount > 1) {
    return "Downgrade to one license before making this change";
  }
  return "Learn more about this tier from the Cabinet Team";
};

const validateUniqueEmail = (email: string, existingEmails: string[]) => {
  if (existingEmails.map(e => e.toLowerCase()).includes(email.toLowerCase())) {
    return 'Emails must be unique';
  }
  return true;
};

const CabButtonDisableOnClick = (props: CabButtonProps) => {
  const [clicked, setClicked] = useState(false);
  return <CabButton {...props}  onClick={(args) => {
    if (props.onClick) props.onClick(args);
    setClicked(true);
  }} disabled={props.disabled || clicked}/>;
};

export type LicenseGridProps = {
  control: Control<GridData>;
  formState: FormState<GridData>;
  setValue: UseFormSetValue<GridData>;
  organization: Partial<Organization> & { id: Organization['id'] };
  handleSubmit: () => Promise<void>;
  getValues: UseFormGetValues<GridData>;
  reset: UseFormReset<GridData>;
  setRows: (values: GridData["data"]) => void;
  setCachedRows: (values: GridData["data"]) => void;
  rows: GridData["data"];
  discardChanges: () => void;
  usedCoreLicenses: number;
  usedStrategicLicenses: number;
  usedEnterpriseLicenses: number;
  setUsedCore: (used: number) => void;
  setUsedStrategic: (used: number) => void;
  setUsedEnterprise: (used: number) => void;
  onCreateLicense: (lic: OrganizationLicense) => Promise<void>;
  user?: User | null;
  handleOpenLicenseModal: () => void;
  org: OrganizationSubmission;
  resendInvite: (license: OrganizationLicense) => void;
  orgLeaders: OrganizationLeader[];
};

type NewUserInfo<T extends USER_ROLE> = {
  info: UserInfo;
  role: T;
  link: T extends USER_ROLE.INDIVIDUAL ? UserLink : IndividualLink;
};

const defaultNewUser: NewUserInfo<USER_ROLE.INDIVIDUAL> | NewUserInfo<USER_ROLE.ASSISTANT> = {
  info: { name: '', email: '', licenseTier: NEW_TIER.BASIC },
  role: USER_ROLE.INDIVIDUAL,
  link: { assistant: 0, leader: 0 },
};

export default function LicenseGrid({
  control, formState, setValue, organization, handleSubmit, getValues, rows, setRows,
  discardChanges, usedCoreLicenses, usedStrategicLicenses, usedEnterpriseLicenses,
  setUsedCore, onCreateLicense, resendInvite, user,
  handleOpenLicenseModal, setCachedRows, reset, org, setUsedStrategic, setUsedEnterprise, orgLeaders
}: LicenseGridProps) {

  const [coreLicenses, setCoreLicenses] = useState(0);
  const [strategicLicenses, setStrategicLicenses] = useState(0);
  const [enterpriseLicenses, setEnterpriseLicenses] = useState(0);
  const [page, setPage] = useState(0);
  const [filterState, setFilterState] = useState<GridFilterModel>();
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>(emptyRowSelectionModel());
  const [newUserStep, setNewUserStep] = useState<number | null>(null);
  const submitHandler = useRef<(() => void) | null>(null);
  const [newUser, setNewUser] = useState(defaultNewUser);
  const [newUserRole, setNewUserRole] = useState<USER_ROLE | null>(null);

  const assistantsLeaders = Object.values(rows)
    .filter(row => row.user_role === USER_ROLE.ASSISTANT)
    .map(row => ({
      [row.user || '']: (row.assists_leaders || []).filter((l) => !l.leader_user)
    }))
    .reduce((a, b) => ({ ...a, ...b }), {});

  useEffect(() => {
    const curUsedCoreLicenses = Object.values(rows).filter(
      row => row.license_tier === NEW_TIER_IDS["CORE"]
    ).length;
    const curUsedStrategicLicenses = Object.values(rows).filter(
      row => row.license_tier === NEW_TIER_IDS["STRATEGIC"]
    ).length;
    const curUsedEnterpriseLicenses = Object.values(rows).filter(
      row => row.license_tier === NEW_TIER_IDS["ENTERPRISE"]
    ).length;
    setUsedCore(curUsedCoreLicenses);
    setUsedStrategic(curUsedStrategicLicenses);
    setUsedEnterprise(curUsedEnterpriseLicenses);
  // We need to look to see if the values of the object changes between instances
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(rows)]);

  useEffect(() => {
    setCoreLicenses(organization.license_count?.core || 0);
    setStrategicLicenses(organization.license_count?.strategic || 0);
    setEnterpriseLicenses(organization.license_count?.enterprise || 0);
  }, [organization.license_count]);

  const createNewInvite = useCallback((info: NewUserInfo<USER_ROLE.INDIVIDUAL> | NewUserInfo<USER_ROLE.ASSISTANT>) => {
    const lowestIdRow = Object.values(rows).sort((a, b) => a.id - b.id)[0];
    const newId = (lowestIdRow && lowestIdRow.id < 0) ? lowestIdRow.id - 1 : -1;

    const newLicense = {
      id: newId,
      email: info.info.email,
      name: info.info.name,
      organization: organization.id,
      license_tier: -1,
      user: null,
      is_active: false,
      org_permissions_groups: [ORGANIZATION_PERMISSION.ANALYTICS_LABELS],
      tier: info.info.licenseTier,
      user_role: info.role,
      created_by: user?.id || -1,
      onsignup_assistant: info.role === USER_ROLE.INDIVIDUAL ? info.link.assistant || null : null,
      onsignup_leaders: info.role === USER_ROLE.INDIVIDUAL
        ? (info.link.leader ? [info.link.leader] : [])
        : info.link.individuals,
    };

    // const newRows: GridData["data"] = {
    //   ...rows, [newId]: newLicense
    // };
    // const key = `data.${newId}`;
    // setValue(key, newLicense, {shouldDirty: true, shouldValidate: false});
    // setRows(newRows);
    onCreateLicense(newLicense);
    setPage(0);
  // }, [setValue, organization.id, setRows, rows]);
  }, [onCreateLicense, organization.id, rows, user?.id]);


  const handleRegisterSubmit = useCallback((submitFunc: () => void) => {
    submitHandler.current = submitFunc;
  }, []);

  const handleSetUserInfo = (userInfo: UserInfo) => {
    setNewUser({ ...newUser, info: userInfo });
    setNewUserStep(newUserStep == null ? 0 : newUserStep + 1);
    submitHandler.current = null;
  };

  const handleSetUserRole = (userRole: USER_ROLE) => {
    if (userRole !== newUser.role) {
      if (userRole === USER_ROLE.INDIVIDUAL) {
        setNewUser({ ...newUser, role: USER_ROLE.INDIVIDUAL, link: { assistant: 0, leader: 0 } });
      } else {
        setNewUser({ ...newUser, role: USER_ROLE.ASSISTANT, link: { individuals: [] } });
      }
    }
    setNewUserStep(newUserStep == null ? 0 : newUserStep + 1);
    submitHandler.current = null;
  };

  const handleLinkAssistant = async (link: UserLink) => {
    createNewInvite({ ...newUser, role: USER_ROLE.INDIVIDUAL, link });
    await handleSubmit();
    setNewUserStep(null);
    setNewUserRole(null);
    setNewUser(defaultNewUser);
    submitHandler.current = null;
  };

  const handleLinkIndividual = async (link: IndividualLink) => {
    createNewInvite({ ...newUser, role: USER_ROLE.ASSISTANT, link });
    await handleSubmit();
    setNewUserRole(null);
    setNewUserStep(null);
    setNewUser(defaultNewUser);
    submitHandler.current = null;
  };

  const handleNewUserNext = () => {
    submitHandler.current?.();
  };

  const handleNewUserCancel = () => {
    submitHandler.current = null;
    setNewUserRole(null);
    setNewUser(defaultNewUser);
    setNewUserStep(null);
  };

  const handleNewUserBack = () => {
    submitHandler.current = null;
    setNewUserStep(newUserStep == null || newUserStep <= 0 ? 0 : newUserStep - 1);
  };

  const renderUnsubmittedChanges = useCallback<(
    (props: GridRenderCellParams<OrganizationLicense, ReactNode>) => ReactNode
  )>(({ row: { user: licenseUser } }) => {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            width:"100%"
          }}>
          {licenseUser ?
            <CabIcon sx={{color: colors.greenPrimary}} size="large"  Icon={IoCheckmarkCircleOutline}/>
            :
            <CabIcon sx={{color: colors.white400}}  size="large"  Icon={IoCloseCircleOutline}/>
          }
        </Box>
      );
    }, []);

  const renderDeleteButton = useCallback<(
    (props: GridRenderCellParams<OrganizationLicense, ReactNode>) => ReactNode
  )>(({ row }) => {
      const removeRecord = () => {
        const dataRows = rows;
        const newRows: {[id: number]: OrganizationLicense} = {};

        Object.values(dataRows).forEach((datum) => {
          if (datum.id !== row.id && datum.id !== 0) {
            newRows[datum.id] = datum;
          }
        });

        const removedRow = dataRows[row.id];

        if (removedRow.license_tier === 100) {
          setUsedCore(usedCoreLicenses - 1);
        } else if (removedRow.license_tier === 102) {
          setUsedStrategic(usedStrategicLicenses - 1);
        } else if (removedRow.license_tier === 103) {
          setUsedEnterprise(usedEnterpriseLicenses - 1);
        }

        const newLicense = {
          ...removedRow,
          id: 0,
        };


        const key = `data.${row.id}`;
        setValue(key, newLicense, { shouldDirty: true, shouldValidate: false });
        setRows(newRows);
      };
      const disabled = row.user !== user?.id;
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            width:"100%",
            ...(!disabled ? {opacity: 0.5} : undefined)
          }}>
          <CabIcon
            color={colors.redError}
            size="large"
            Icon={IoTrashOutline}
            onClick={() => disabled ? removeRecord() : undefined}
          />
        </Box>
      );
    }, [
      user?.id, rows, setValue, setRows, setUsedCore,
      usedCoreLicenses, setUsedStrategic, usedStrategicLicenses, setUsedEnterprise, usedEnterpriseLicenses
    ]);

  const renderEmailCell = useCallback<(
    (props: GridRenderCellParams<OrganizationLicense, ReactNode>) => ReactNode
  )>(({ row }) => {

      if (row.user) {
        return <Typography>{row.email}</Typography>;
      }

      return (
        <Controller
          control={control}
          name={`data.${row.id}.email`}
          rules={{
            required: true,
            pattern: emailRegex,
            validate: (email) => validateUniqueEmail(
              email,
              Object.values(getValues('data') || {}).filter(v => v.id !== row.id).map(v => v.email),
            ),
          }}
          render={
            ({field: {value, onChange}, fieldState: {error}}) => <Box sx={{
              position: "relative"
            }}><CabTextInput
                value={value}
                onChange={onChange}
                disabled={row.user === user?.id}
                formControlSx={{width: error ? "230px" : "280px"}}
              />
              {error && <CabTooltip title={error.message || ""} placement="top">
                <Alert
                  severity='error'
                  sx={{
                    position: "absolute",
                    padding: "2px 16px",
                    top: 0,
                    right: -50,
                    "& .MuiAlert-icon": {marginRight: 0}
                  }}
                />
              </CabTooltip>}
            </Box>
          }
        />
      );
    }, [control, getValues, user?.id]);

  const licenseCount = useMemo(
    () => coreLicenses + strategicLicenses + enterpriseLicenses,
    [strategicLicenses, coreLicenses, enterpriseLicenses]
  );

  const currentTier = useMemo(
    () => {
      if (coreLicenses > 0) {
        return NEW_TIER.CORE;
      } else if (strategicLicenses > 0) {
        return NEW_TIER.STRATEGIC;
      } else if (enterpriseLicenses > 0) {
        return NEW_TIER.ENTERPRISE;
      }
      return NEW_TIER.BASIC;
    },

    [strategicLicenses, coreLicenses, enterpriseLicenses]
  );

  const getLicenseTierTooltip = useCallback((tier: TIER) => {
    if (tier === NEW_TIER.CORE) {
      return dropDownToAddLicenseText(currentTier, NEW_TIER.CORE, coreLicenses <= usedCoreLicenses, licenseCount);
    }

    if (tier === NEW_TIER.STRATEGIC) {
      return dropDownToAddLicenseText(
        currentTier, NEW_TIER.STRATEGIC, strategicLicenses <= usedStrategicLicenses, licenseCount
      );
    }

    if (tier === NEW_TIER.ENTERPRISE) {
      return dropDownToAddLicenseText(
        currentTier, NEW_TIER.ENTERPRISE, enterpriseLicenses <= usedEnterpriseLicenses, licenseCount
      );
    }

    return undefined;
  }, [
    coreLicenses, currentTier, enterpriseLicenses, licenseCount, strategicLicenses,
    usedCoreLicenses, usedEnterpriseLicenses, usedStrategicLicenses
  ]);

  const buildLicenseTierOptions = useCallback((
    getTierIsDisabled: (tier: TIER) => boolean
  ): CabDropdownProps<TIER>["options"] => [
    {
      value: NEW_TIER.BASIC,
      label: "No License",
      disabled: getTierIsDisabled(NEW_TIER.BASIC)
    },
    {
      value: NEW_TIER.CORE,
      label: NEW_TIER.CORE,
      disabled: getTierIsDisabled(NEW_TIER.CORE),
      tooltipText: getLicenseTierTooltip(NEW_TIER.CORE)
    },
    {
      value: NEW_TIER.STRATEGIC,
      label: NEW_TIER.STRATEGIC,
      disabled: getTierIsDisabled(NEW_TIER.STRATEGIC),
      tooltipText: getLicenseTierTooltip(NEW_TIER.STRATEGIC)
    },
    {
      value: NEW_TIER.ENTERPRISE,
      label: NEW_TIER.ENTERPRISE,
      disabled: getTierIsDisabled(NEW_TIER.ENTERPRISE),
      tooltipText: getLicenseTierTooltip(NEW_TIER.ENTERPRISE)
    }
  ], [getLicenseTierTooltip]);

  const selectedLicenseIds = useMemo(
    () => getSelectedRowIds(rowSelectionModel).map(id => Number(id)),
    [rowSelectionModel]
  );

  const hasSelectedRows = selectedLicenseIds.length > 0;

  const selectedRows = useMemo(
    () => Object.values(rows).filter(row => selectedLicenseIds.includes(row.id)),
    [rows, selectedLicenseIds]
  );

  const getBulkTierIsDisabled = useCallback((tier: TIER) => {
    if (!hasSelectedRows || tier === NEW_TIER.BASIC) {
      return false;
    }

    const tierId = NEW_TIER_IDS[tier];
    const currentlyUsingTier = selectedRows.filter(row => row.license_tier === tierId).length;
    const selectedNeedingTier = selectedRows.length - currentlyUsingTier;

    if (tier === NEW_TIER.CORE) {
      return licenseCount > 1 || usedCoreLicenses + selectedNeedingTier > coreLicenses;
    }

    if (tier === NEW_TIER.STRATEGIC) {
      return usedStrategicLicenses + selectedNeedingTier > strategicLicenses;
    }

    if (tier === NEW_TIER.ENTERPRISE) {
      return usedEnterpriseLicenses + selectedNeedingTier > enterpriseLicenses;
    }

    return false;
  }, [
    coreLicenses, enterpriseLicenses, hasSelectedRows, licenseCount, selectedRows,
    strategicLicenses, usedCoreLicenses, usedEnterpriseLicenses, usedStrategicLicenses
  ]);

  const bulkLicenseTierOptions = useMemo<CabDropdownProps<TIER | ''>["options"]>(
    () => buildLicenseTierOptions(getBulkTierIsDisabled) as CabDropdownProps<TIER | ''>["options"],
    [buildLicenseTierOptions, getBulkTierIsDisabled]
  );

  const handleBulkTierChange = useCallback((event: SelectChangeEvent<TIER | ''>) => {
    const tier = event.target.value as TIER;
    if (!tier) return;
    if (getBulkTierIsDisabled(tier)) return;

    const selectedIds = new Set(selectedLicenseIds);
    const licenseTier = NEW_TIER_IDS[tier];
    const newRows = { ...rows };

    Object.values(rows).forEach((row) => {
      if (!selectedIds.has(row.id)) return;

      const updatedRow = {
        ...row,
        tier,
        license_tier: licenseTier,
      };

      newRows[row.id] = updatedRow;
      const key = `data.${row.id}`;
      setValue(key, updatedRow, { shouldDirty: true, shouldValidate: false });
    });

    setRows(newRows);
    setRowSelectionModel(emptyRowSelectionModel());
  }, [getBulkTierIsDisabled, rows, selectedLicenseIds, setRows, setValue]);

  const handleBulkDelete = useCallback(() => {
    const selectedIds = new Set(selectedLicenseIds);
    const newRows: { [id: number]: OrganizationLicense } = {};

    Object.values(rows).forEach((row) => {
      const canDeleteRow = selectedIds.has(row.id) && row.user !== user?.id;

      if (canDeleteRow) {
        const key = `data.${row.id}`;
        setValue(key, { ...row, id: 0 }, { shouldDirty: true, shouldValidate: false });
        return;
      }

      if (row.id !== 0) {
        newRows[row.id] = row;
      }
    });

    setRows(newRows);
    setRowSelectionModel(emptyRowSelectionModel());
  }, [rows, selectedLicenseIds, setRows, setValue, user?.id]);

  const renderTierCell = useCallback<(
    (props: GridRenderCellParams<OrganizationLicense, ReactNode>) => ReactNode
  )>(({row}) => {
      const disabled = false;
      return <>
        <Controller
          control={control}
          name={`data.${row.id}.tier`}
          render={
            ({field: {value, onChange}}) => {
              const rowLicenseTierOptions = buildLicenseTierOptions((tier) => (
                tier !== value && (
                  (tier === NEW_TIER.CORE && (coreLicenses <= usedCoreLicenses || licenseCount > 1)) ||
                  (tier === NEW_TIER.STRATEGIC && strategicLicenses <= usedStrategicLicenses) ||
                  (tier === NEW_TIER.ENTERPRISE && enterpriseLicenses <= usedEnterpriseLicenses)
                )
              ));

              const manageLicenseStateOnChange = (e: SelectChangeEvent<TIER>) => {
                let licenseTier = -1;
                let tier = NEW_TIER.BASIC;
                if (value === NEW_TIER.CORE) {
                  setUsedCore(usedCoreLicenses - 1);
                } else if (value === NEW_TIER.STRATEGIC) {
                  setUsedStrategic(usedStrategicLicenses - 1);
                } else if (value === NEW_TIER.ENTERPRISE) {
                  setUsedEnterprise(usedEnterpriseLicenses - 1);
                }

                if (e.target.value === NEW_TIER.CORE) {
                  const newCoreCount = usedCoreLicenses + 1;
                  licenseTier = NEW_TIER_IDS["CORE"];
                  tier = NEW_TIER.CORE;
                  if (newCoreCount > coreLicenses) {
                    setCachedRows(rows);
                    handleOpenLicenseModal();
                  } else {
                    setUsedCore(usedCoreLicenses + 1);
                  }
                } else if (e.target.value === NEW_TIER.STRATEGIC) {
                  licenseTier = NEW_TIER_IDS["STRATEGIC"];
                  tier = NEW_TIER.STRATEGIC;
                  setUsedStrategic(usedStrategicLicenses + 1);
                } else if (e.target.value === NEW_TIER.ENTERPRISE) {
                  licenseTier = NEW_TIER_IDS["ENTERPRISE"];
                  tier = NEW_TIER.ENTERPRISE;
                  setUsedEnterprise(usedEnterpriseLicenses + 1);
                }

                onChange(e);
                setRows({...rows, [row.id]: {...rows[row.id], license_tier: licenseTier, tier: tier}});
              };

              return <CabDropdown<TIER>
                value={value ? value : NEW_TIER.BASIC}
                disabled={disabled}
                sx={{ width: "100%" }}
                onChange={manageLicenseStateOnChange}
                options={rowLicenseTierOptions}
              />;
            }
          }
        />
      </>;
    }, [
      control, setRows, rows, setUsedCore, usedCoreLicenses,
      coreLicenses, setCachedRows, handleOpenLicenseModal, licenseCount,
      setUsedStrategic, usedStrategicLicenses, strategicLicenses,
      setUsedEnterprise, usedEnterpriseLicenses, enterpriseLicenses, buildLicenseTierOptions
    ]);

  const renderUserRoleCell = useCallback<(
    (props: GridRenderCellParams<OrganizationLicense, ReactNode>) => ReactNode
  )>(({ row }) => {
      const tooltip = `To change this account's role, please contact help@joincabinet.com`;
      const userRoleOptions = [
        { value: USER_ROLE.ASSISTANT, label: "Yes", disabled: false },
        { value: USER_ROLE.INDIVIDUAL, label: "No", disabled: false },
      ];
      const disabled = false;
      return <>
        <Controller
          control={control}
          name={`data.${row.id}.user_role`}
          render={
            ({ field: { value, onChange } }) => {

              return <CabTooltip
                title={disabled ? tooltip : ''}
                placement="bottom"
                wrapWithSpan
                sx={{width: '100%'}}
              >
                <CabDropdown<USER_ROLE>
                  value={value ? value : USER_ROLE.INDIVIDUAL}
                  disabled={disabled}
                  sx={{ width: "100%" }}
                  onChange={onChange}
                  options={userRoleOptions}
                />
              </CabTooltip>;
            }
          }
        />
      </>;
    }, [control]);

  const renderOrgPermissionRoles = useCallback<(
    (props: GridRenderCellParams<OrganizationLicense, ReactNode>) => ReactNode
  )>(({ row }) => {
      const tooltip = `
      To change this account's role, please contact an administrator or manager with your organization
      `;
      const orgPermissionOptions = [
        {
          value: ORGANIZATION_PERMISSION.ADMIN,
          label: "Admin",
          disabled: !user?.active_license.org_permissions_groups.includes(ORGANIZATION_PERMISSION.ADMIN),
          subtext: "Full permissions to the organization, including changing roles for other users."
        },
        {
          value: ORGANIZATION_PERMISSION.BILLING,
          label: "Billing",
          disabled: !(
            user?.active_license.org_permissions_groups.includes(ORGANIZATION_PERMISSION.ADMIN) ||
            user?.active_license.org_permissions_groups.includes(ORGANIZATION_PERMISSION.MANAGER)
          ),
          subtext: "Can see and change billing information and subscription details"
        },
        {
          value: ORGANIZATION_PERMISSION.MANAGER,
          label: "Team",
          disabled: !(
            user?.active_license.org_permissions_groups.includes(ORGANIZATION_PERMISSION.ADMIN) ||
            user?.active_license.org_permissions_groups.includes(ORGANIZATION_PERMISSION.MANAGER)
          ),
          subtext: "Can manage users"
        },
        {
          value: ORGANIZATION_PERMISSION.ANALYTICS_LABELS,
          label: "Analytics Labels",
          disabled: !(
            user?.active_license.org_permissions_groups.includes(ORGANIZATION_PERMISSION.ADMIN) ||
            user?.active_license.org_permissions_groups.includes(ORGANIZATION_PERMISSION.MANAGER)
          ),
          subtext: "Can create, edit or delete calendar analytics labels"
        },
      ];

      const disabled = row.user === user?.id || (
        row.org_permissions_groups.includes(ORGANIZATION_PERMISSION.ADMIN) &&
        !user?.active_license.org_permissions_groups.includes(ORGANIZATION_PERMISSION.ADMIN)
      );

      return <>
        <Controller
          control={control}
          name={`data.${row.id}.org_permissions_groups`}
          render={
            ({ field: { value, onChange } }) => {
              return <CabTooltip
                title={disabled ? tooltip : ''}
                placement="bottom"
                wrapWithSpan
                sx={{ width: '100%' }}
              >
                <CabDropdown<ORGANIZATION_PERMISSION>
                  value={value ? value : []}
                  sx={{ width: "100%" }}
                  disabled={disabled}
                  placeholder='Add permissions'
                  multiple
                  menuItemSx={{maxWidth: "400px", whiteSpace: "pre-wrap"}}
                  onChange={(event) => disabled ? undefined : onChange(event.target?.value)}
                  options={orgPermissionOptions}
                />
              </CabTooltip>;
            }
          }
        />
      </>;
    }, [control, user?.active_license.org_permissions_groups, user?.id]);

  const unCommitedChanges = useCallback<(
    (props: GridRenderCellParams<OrganizationLicense, ReactNode>) => ReactNode
  )>(({row}) => {
      return <>
        {formState.dirtyFields.data && formState.dirtyFields.data[row.id] ?
          <Typography sx={{color: colors.white600}}>Changes Pending</Typography>
          :
          <></>
        }
      </>;
    }, [formState.dirtyFields.data]);


  const renderNameCell = useCallback(
    (props: GridRenderCellParams<OrganizationLicense, ReactNode>): ReactNode => {

      return (props.row.user || !organization.is_on_contract) ?
        <>{props.row.name}</> : <CabButtonDisableOnClick onClick={() => resendInvite(props.row)}>
          Resend Invite
        </CabButtonDisableOnClick>;
    }, [organization.is_on_contract, resendInvite]);

  const getLastLoginDate = useCallback((row: OrganizationLicense): string | null => {
    return row.last_login ?? null;
  }, []);

  const renderLastLoginCell = useCallback<(
    (props: GridRenderCellParams<OrganizationLicense, ReactNode>) => ReactNode
  )>(({ row }) => {
      const lastLoginDate = getLastLoginDate(row);
      if (!lastLoginDate) return <Typography color={colors.black500}>Never</Typography>;

      const formattedDate = DateTime.fromISO(lastLoginDate);
      return <Typography>{formattedDate.isValid ? formattedDate.toFormat('MMM d, yyyy') : lastLoginDate}</Typography>;
    }, [getLastLoginDate]);

  const userIsOrgAdmin = !!user?.active_license.org_permissions_groups.includes(ORGANIZATION_PERMISSION.ADMIN);

  const licenseTierFilterOptions = useMemo(() => (
    Object.values(NEW_TIER).map((tier) => ({
      value: tier,
      label: NEW_TIER_DETAILS[tier].title,
    }))
  ), []);

  const userRoleFilterOptions = useMemo(() => ([
    { value: USER_ROLE.ASSISTANT, label: 'Yes' },
    { value: USER_ROLE.INDIVIDUAL, label: 'No' },
  ]), []);

  const columns: GridColDef[] = useMemo(() => [
    {
      field: 'name',
      headerName: 'Name',
      width: 225,
      display: "flex",
      editable: false,
      sortable: true,
      renderCell: renderNameCell
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 300,
      display: "flex",
      editable: false,
      sortable: true,
      renderCell: renderEmailCell
    },
    ...(user?.active_license.tier !== NEW_TIER.CORE ? [{
      field: 'license_tier',
      headerName: 'License Tier',
      width: 175,
      display: "flex",
      editable: false,
      sortable: true,
      type: 'singleSelect',
      valueOptions: licenseTierFilterOptions,
      valueGetter: (value, row) => {
        void value;
        return row.tier ?? NEW_TIER.BASIC;
      },
      renderCell: renderTierCell
    } satisfies GridColDef] : []),
    ...(userIsOrgAdmin ? [{
      field: 'last_login',
      headerName: 'Last Login',
      width: 150,
      display: "flex",
      editable: false,
      sortable: true,
      valueGetter: (value, row) => {
        void value;
        return getLastLoginDate(row);
      },
      renderCell: renderLastLoginCell
    } satisfies GridColDef] : []),
    {
      field: 'user_role',
      headerName: 'Is Assistant?',
      width: 175,
      display: "flex",
      editable: false,
      sortable: true,
      type: 'singleSelect',
      valueOptions: userRoleFilterOptions,
      renderCell: renderUserRoleCell
    },
    {
      field: 'permissions',
      headerName: 'Org Permissions',
      width: 175,
      display: "flex",
      editable: false,
      sortable: true,
      filterable: false,
      renderCell: renderOrgPermissionRoles
    },
    {
      width: 130,
      display: "flex",
      editable: false,
      groupable: false,
      sortable: false,
      align:"right",
      headerAlign: "right",
      pinnable: false,
      disableColumnMenu: true,
      resizable: false,
      filterable: false,
      renderCell: renderUnsubmittedChanges,
      field: 'submitted',
      headerName: 'Account Created',
    },
    {
      field: 'remove',
      headerName: 'Remove',
      width: 72,
      display: "flex",
      editable: false,
      sortable: false,
      filterable: false,
      renderCell: renderDeleteButton
    },
    {
      field: 'uncomitted',
      headerName: '',
      width: 150,
      display: "flex",
      editable: false,
      sortable: false,
      filterable: false,
      renderCell: unCommitedChanges
    },
    {
      field: '',
      headerName: '',
      minWidth: 0,
      display: "flex",
      flex: 1,
      disableColumnMenu: true,
      resizable: false,
      hideable: false,
      filterable: false,
      sortable: false,
    },
  ], [
    getLastLoginDate, licenseTierFilterOptions, renderDeleteButton, renderEmailCell, renderLastLoginCell,
    renderNameCell, renderOrgPermissionRoles, renderTierCell, renderUnsubmittedChanges, renderUserRoleCell,
    unCommitedChanges, user?.active_license.tier, userIsOrgAdmin, userRoleFilterOptions
  ]);

  const dataRows = useMemo(() => (
    Object.values(rows).sort((a, b) => a.id - b.id)
  ), [rows]);

  const paginationModel = useMemo(() => ({
    page,
    pageSize: 10,
  }), [page]);

  const loading = useMemo(() => (Object.keys(rows).length === 0), [rows]);

  const slots = useMemo<DataGridProProps["slots"]>(() => ({
    noRowsOverlay: () => (
      <Stack
        sx={{
          height: "100%",
          alignItems: "center",
          justifyContent: "center"
        }}>
        There are no users in your organization
      </Stack>
    ),
    toolbar: () => <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        margin: 1,
        width: "100%",
        paddingRight:2
      }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1
        }}>
        <CabButton type="button" onClick={() => setNewUserStep(0)}>
          Add User
        </CabButton>
        <CabDropdown<TIER | ''>
          size="small"
          value=""
          placeholder="Change Tier Type"
          disabled={!hasSelectedRows}
          sx={{ minWidth: 175 }}
          options={bulkLicenseTierOptions}
          onChange={handleBulkTierChange}
        />
        <CabButton
          type="button"
          buttonType="tertiary"
          disabled={!hasSelectedRows}
          onClick={handleBulkDelete}
        >
          Delete
        </CabButton>
        <GridToolbarFilterButton />
        <GridToolbarColumnsButton />
      </Box>
      <Box>
        <CabButton
          type="button"
          buttonType='tertiary'
          sx={{marginRight: 1}}
          disabled={!formState.isDirty}
          onClick={() => discardChanges()}
        >
          Discard Changes
        </CabButton>
        <CabButton
          type="button"
          disabled={!formState.isDirty}
          onClick={() => handleSubmit()}
        >
          Apply Changes
        </CabButton>
      </Box>
    </Box>
  }), [
    bulkLicenseTierOptions, discardChanges, formState.isDirty, handleBulkDelete,
    handleBulkTierChange, handleSubmit, hasSelectedRows
  ]);

  return (
    <>
      <CabDataGrid
        rows={dataRows}
        columns={columns}
        pagination
        paginationModel={paginationModel}
        autoHeight
        pageSizeOptions={[10]}
        onPaginationModelChange={(model) => setPage(model.page)}
        disableRowSelectionOnClick
        loading={loading}
        showToolbar={!!slots?.toolbar}
        slots={slots}
        filterMode="client"
        filterModel={filterState}
        onFilterModelChange={(data) => setFilterState(data)}
        checkboxSelection
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={setRowSelectionModel}
        sx={{
          "& .MuiDataGrid-columnHeader": {
            backgroundColor: 'unset'
          },
          "&": {
            border: `1px solid ${colors.black100}`
          },
          "& .MuiDataGrid-virtualScroller": {
            border: 0
          }
        }}
      />
      <CabModal
        open={newUserStep != null}
        onClose={handleNewUserCancel}
        title="Add User"
        text={newUserStep === 1
          ? 'How will this person use Cabinet?'
          : (newUserStep === 2
            ? (newUser.role === USER_ROLE.INDIVIDUAL
              ? "If this user has an assistant on Cabinet already, you can connect them below"
              : "Does this assistant support any Individual who already has a Cabinet account?")
            : '')}
        sx={{ '& .MuiDialog-paper': { width: 690 } }}
        actionButtons={(
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%"
            }}>
            {(newUserStep || 0) > 0 ? (
              <CabButton
                onClick={handleNewUserBack}
                buttonType="tertiary"
                icon={<CabIcon Icon={IoArrowBack} />}
              >
                Back
              </CabButton>
            ) : <Box />}
            <Box
              sx={{
                display: "flex",
                gap: 1
              }}>
              <CabButton onClick={handleNewUserCancel} buttonType="secondary">Cancel</CabButton>
              <CabButton
                onClick={handleNewUserNext}
                disabled={newUserStep === 1 && newUserRole === null}
              >
                Continue
              </CabButton>
            </Box>
          </Box>
        )}
      >
        {newUserStep === 0 && (
          <AddUserInfo
            defaultInfo={newUser.info}
            isStrategicOrganization={strategicLicenses > 0}
            isEnterpriseOrganization={enterpriseLicenses > 0}
            hasCoreLicensesAvailable={coreLicenses > usedCoreLicenses}
            hasStrategicLicensesAvailable={strategicLicenses > usedStrategicLicenses}
            hasEnterpriseLicensesAvailable={enterpriseLicenses > usedEnterpriseLicenses}
            existingEmails={Object.values(getValues('data') || {}).map(v => v.email)}
            onRegisterSubmit={handleRegisterSubmit}
            onSubmit={handleSetUserInfo}
            handleOpenLicenseModal={handleOpenLicenseModal}
            licenseCount={licenseCount}
            currentTier={currentTier}
          />
        )}
        {newUserStep === 1 && (
          <AddUserRole
            defaultRole={newUserRole}
            onRegisterSubmit={handleRegisterSubmit}
            onSubmit={handleSetUserRole}
            setNewUserRole={setNewUserRole}
          />
        )}
        {newUserStep === 2 && (newUser.role === USER_ROLE.INDIVIDUAL ? (
          <AddUserLinkAssistant
            defaultUserLink={newUser.link}
            onRegisterSubmit={handleRegisterSubmit}
            onSubmit={handleLinkAssistant}
            assistants={Object.values(rows).filter(a => a.user_role === USER_ROLE.ASSISTANT)}
            assistantsLeaders={assistantsLeaders}
          />
        ) : (
          <AddUserLinkIndividual
            defaultIndividualLink={newUser.link}
            onRegisterSubmit={handleRegisterSubmit}
            onSubmit={handleLinkIndividual}
            individuals={Object.values(rows).filter(a => a.user_role === USER_ROLE.INDIVIDUAL)}
            orgLeaders={orgLeaders}
          />
        ))}
      </CabModal>
    </>
  );
}

type UserInfo = {
  name: string;
  email: string;
  licenseTier: TIER;
};

interface AddUserInfoProps {
  defaultInfo: UserInfo;
  hasCoreLicensesAvailable: boolean;
  hasStrategicLicensesAvailable: boolean;
  hasEnterpriseLicensesAvailable: boolean;
  existingEmails: string[],
  onRegisterSubmit: (submitFunc: () => void) => void;
  onSubmit: (userInfo: UserInfo) => void;
  handleOpenLicenseModal: () => void
  licenseCount: number
  currentTier: NEW_TIER
  isEnterpriseOrganization: boolean
  isStrategicOrganization: boolean
}

const licenseUnavailableText = (tier: TIER) => {
  const tierName = tier === NEW_TIER.STRATEGIC ? 'Strategic' : 'Core';
  return `There are no more ${tierName} Licenses available.
  You may add more through the Licenses table.`;
};

const AddUserInfo = ({
  defaultInfo, hasCoreLicensesAvailable, existingEmails, onRegisterSubmit, onSubmit,
  handleOpenLicenseModal, licenseCount, currentTier, hasEnterpriseLicensesAvailable,
  hasStrategicLicensesAvailable, isStrategicOrganization, isEnterpriseOrganization
}: AddUserInfoProps) => {
  const { control, handleSubmit } = useForm({ defaultValues: defaultInfo });

  useEffect(() => onRegisterSubmit(handleSubmit(onSubmit)), [onRegisterSubmit, handleSubmit, onSubmit]);

  const licenseTierOptions = useMemo(() => {
    const coreDisabled = !hasCoreLicensesAvailable;
    const strategicDisabled = !hasStrategicLicensesAvailable || !isStrategicOrganization;
    const enterpriseDisabled = !hasEnterpriseLicensesAvailable || !isEnterpriseOrganization;
    const licenseTierOptionsList = [
      { value: NEW_TIER.BASIC, label: "No License", disabled: false },
      {
        value: NEW_TIER.CORE,
        label: "Core",
        disabled: coreDisabled,
        tooltipText: hasCoreLicensesAvailable ? '' : (
          !coreDisabled ? dropDownToAddLicenseText(
            currentTier, NEW_TIER.CORE, !coreDisabled, licenseCount
          ) :
            licenseUnavailableText(NEW_TIER.CORE)
        ),
      },
      {
        value: NEW_TIER.STRATEGIC,
        label: "Strategic",
        disabled: strategicDisabled,
        tooltipText: hasStrategicLicensesAvailable ? '' : (
          !strategicDisabled ?
            dropDownToAddLicenseText(currentTier, NEW_TIER.STRATEGIC, !strategicDisabled, licenseCount) :
            licenseUnavailableText(NEW_TIER.STRATEGIC, )
        ),
      },
      {
        value: NEW_TIER.ENTERPRISE,
        label: "Enterprise",
        disabled: enterpriseDisabled,
        tooltipText: hasEnterpriseLicensesAvailable ? '' : (
          !enterpriseDisabled ?
            dropDownToAddLicenseText(currentTier, NEW_TIER.ENTERPRISE, !enterpriseDisabled, licenseCount) :
            licenseUnavailableText(NEW_TIER.ENTERPRISE, )
        ),
      },
    ];

    return licenseTierOptionsList;
  }, [
    currentTier, hasCoreLicensesAvailable,
    licenseCount, hasStrategicLicensesAvailable, hasEnterpriseLicensesAvailable,
    isEnterpriseOrganization, isStrategicOrganization
  ]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={2}>
        <FormControl>
          <FormLabel>Email</FormLabel>
          <Controller control={control} name="email"
            rules={{
              pattern: { value: emailRegex, message: 'Must be a valid email' },
              validate: (email) => validateUniqueEmail(email, existingEmails),
              required: { value: true, message: 'Required' },
            }}
            render={({ field: { ref, ...field }, fieldState }) => (<>
              <CabTextInput {...field} inputRef={ref} />
              <FormHelperText error>{fieldState.error && fieldState.error.message}</FormHelperText>
            </>)}
          />
        </FormControl>
        <FormControl>
          <FormLabel>License Tier</FormLabel>
          <Controller control={control} name="licenseTier" render={({ field: { ref, onChange, ...field } }) => (
            <CabDropdown<TIER>
              {...field}
              sx={{ width: "100%" }}
              options={licenseTierOptions}
              onChange={(e) => {
                if (e.target.value === NEW_TIER.BASIC) {
                  onChange(e);
                } else if (e.target.value === NEW_TIER.CORE) {
                  if (hasCoreLicensesAvailable) {
                    onChange(e);
                  } else {
                    handleOpenLicenseModal();
                  }
                } else if (e.target.value === NEW_TIER.STRATEGIC) {
                  if (hasStrategicLicensesAvailable) {
                    onChange(e);
                  }
                } else if (e.target.value === NEW_TIER.ENTERPRISE) {
                  if (hasEnterpriseLicensesAvailable) {
                    onChange(e);
                  }
                }
              }}
            />
          )} />
        </FormControl>
      </Stack>
      <input type="submit" hidden />
    </form>
  );
};

interface AddUserRoleProps {
  defaultRole: USER_ROLE | null;
  onRegisterSubmit: (submitFunc: () => void) => void;
  onSubmit: (userRole: USER_ROLE) => void;
  setNewUserRole: (role: USER_ROLE) => void;
}

const AddUserRole = ({ defaultRole, onRegisterSubmit, onSubmit, setNewUserRole }: AddUserRoleProps) => {
  const [userRole, setUserRole] = useState<USER_ROLE | null>(defaultRole);

  useEffect(() => setUserRole(defaultRole), [defaultRole]);

  useEffect(() => {
    if (userRole != null) {
      setNewUserRole(userRole);
    }
  }, [setNewUserRole, userRole]);

  const handleSubmit = useCallback(() => {
    if (userRole != null) {
      onSubmit(userRole);
    }
  }, [onSubmit, userRole]);

  useEffect(() => onRegisterSubmit(handleSubmit), [onRegisterSubmit, handleSubmit]);

  const userRoleOptions = [
    {
      id: USER_ROLE.INDIVIDUAL,
      type: USER_ROLE.INDIVIDUAL,
      caption: 'Best for executives, sales, recruiting, founders, and other professionals.',
      details: [],
    },
    {
      id: USER_ROLE.ASSISTANT,
      type: USER_ROLE.ASSISTANT,
      caption: "Best for assistants, admins, and others in supporting roles.",
      details: [],
    },
  ];

  return (
    <Box>
      <CabCardSelect<USER_ROLE>
        options={userRoleOptions}
        onChange={(v) => setUserRole(v)}
        value={userRole || undefined}
        title="User is using Cabinet..."
        individualTitle="For Themselves"
        assistantTitle="For Someone Else"
      />
    </Box>
  );
};

type UserLink = {
  assistant: number | null;
  leader: number | null;
};

interface AddUserLinkUsersProps {
  defaultUserLink: UserLink;
  onRegisterSubmit: (submitFunc: () => void) => void;
  onSubmit: (link: UserLink) => void;
  assistants: OrganizationLicense[];
  assistantsLeaders: { [id: number]: Leader[] };
}

const AddUserLinkAssistant = ({
  defaultUserLink, onRegisterSubmit, onSubmit, assistants, assistantsLeaders,
}: AddUserLinkUsersProps) => {
  const { control, handleSubmit, watch } = useForm({ defaultValues: defaultUserLink });
  const assistant = watch('assistant');

  useEffect(() => onRegisterSubmit(handleSubmit(onSubmit)), [onRegisterSubmit, handleSubmit, onSubmit]);

  return (
    <Stack spacing={2} sx={{
      marginTop: 3
    }}>
      <FormControl>
        <FormLabel>Does this user have an assistant on Cabinet?</FormLabel>
        <Controller control={control} name="assistant"
          rules={{ required: { value: true, message: 'Required' } }}
          render={({ field: { ref, ...field }, fieldState }) => (<>
            <CabDropdown
              placeholder="No"
              options={assistants.map(a => ({
                value: a.user || `org-license-${a.id}`,
                label: a.name || a.email,
                disabled: !a.user,
                tooltipText: a.user ? '' : 'Assistant may not be associated until they have created an account',
              }))}
              {...field}
              value={field.value || ''}
            />
            <FormHelperText error>{fieldState.error && fieldState.error.message}</FormHelperText>
          </>)}
        />
      </FormControl>
      {!!assistant && (
        <FormControl>
          <FormLabel>Does their assistant have an existing profile for them?</FormLabel>
          <Controller control={control} name="leader"
            render={({ field: { ref, ...field }, fieldState }) => (<>
              <CabDropdown
                placeholder="No, create a new one"
                options={assistantsLeaders[assistant]?.map(l => ({
                  value: l.id,
                  label: `${l.first_name} ${l.last_name}`
                })) || []}
                {...field} value={field.value || ''}
              />
              <FormHelperText error>{fieldState.error && fieldState.error.message}</FormHelperText>
            </>)}
          />
        </FormControl>
      )}
    </Stack>
  );
};

type IndividualLink = {
  individuals: number[];
};

interface AddUserLinkIndividualProps {
  defaultIndividualLink: IndividualLink;
  onRegisterSubmit: (submitFunc: () => void) => void;
  onSubmit: (link: IndividualLink) => void;
  individuals: OrganizationLicense[];
  orgLeaders: OrganizationLeader[];
}

type OrganizationLeaderWithProfile = Omit<OrganizationLeader, "user_profile">
& Pick<Required<OrganizationLeader>, "user_profile">;

const AddUserLinkIndividual = ({
  defaultIndividualLink, onRegisterSubmit, onSubmit, individuals, orgLeaders
}: AddUserLinkIndividualProps) => {
  const { control, handleSubmit } = useForm({ defaultValues: defaultIndividualLink });

  useEffect(() => onRegisterSubmit(handleSubmit(onSubmit)), [onRegisterSubmit, handleSubmit, onSubmit]);

  const userToOrgLeader = useMemo(() => (
    Object.fromEntries(orgLeaders
      .filter((ol): ol is OrganizationLeaderWithProfile => ol.user_profile != null)
      .map(ol => [ol.user_profile?.id, ol]))
  ), [orgLeaders]);

  const options = individuals.map(lic => ({
    ...lic,
    leader: lic.user ? userToOrgLeader[lic.user] : null
  })).map(l => ({
    value: l.leader?.id || -1 * l.id,
    label: l.leader ? `${l.leader.first_name} ${l.leader.last_name}` : l.name || l.email,
    disabled: !l.leader,
    tooltipText: !l.leader ? 'User may not be associated until they have created a leader' : ""
  }));

  const getPlaceholder = useCallback((value:  number[]) => (
    options.filter(option => !value.includes(option.value)).length === 0
      ? 'No available individuals'
      : options.filter(option => value.includes(option.value)).length ?
        "Add Another Supported Individual" : "No"
  ), [options]);

  return (
    <Stack spacing={2} sx={{
      marginTop: 3
    }}>
      <FormControl>
        {/* <FormLabel>Assign Individual</FormLabel> */}
        <Controller name="individuals" control={control} render={({ field }) => (
          <>
            {options.filter(option => field.value.includes(option.value)).length ? (
              <CabList
                items={options
                  .filter(option => field.value.includes(option.value)).map(({ value, label }) => ({
                    value,
                    label,
                    editable: true,
                    // disabled: !isOwner,
                    onDeleteClick: () => field.onChange(field.value.filter(v => v !== value)),
                  }))}
              />
            ) : <></>}
            <CabDropdown<number | string>
              placeholder={getPlaceholder(field.value)}
              disabled={options.filter(option => !field.value.includes(option.value)).length === 0}
              options={options.filter(option => !field.value.includes(option.value))}
              onChange={(e) => field.onChange(
                Array.from(new Set([...field.value, e.target.value as number]))
              )}
              value={''}
              sx={{ width: '100%', marginTop: 1, marginBottom: 1 }}
            />
          </>
        )} />
      </FormControl>
    </Stack>
  );
};
